"use client";

import {useTranslation} from "react-i18next";
import {isPresentationRecord, presentedFields, type PresentedField} from "@repo/ui/gailgpt/tool-presentation";
import {usePhoneScriptNames} from "@/hooks/usePhoneScriptNames";
import FrozenValue from "./FrozenValue";
import {ReviewFields, type ToolDetailsProps} from "./ToolPresentationDetails";
import {occurrenceKeys, valueKey} from "./snapshotKeys";

function ScheduleScripts({value, path, fields, names}: {
   value: unknown;
   path: string[];
   fields: ToolDetailsProps["presentation"]["fields"];
   names: ReadonlyMap<string, string>;
}) {
   if (!Array.isArray(value) || !value.length) return <FrozenValue value={value} humanizeLabels />;
   return <ul>{occurrenceKeys(value, valueKey).map(({item: id, key}, scriptIndex) => {
      const metadata = fields.find(item => item.format === "entity" && item.path.length === path.length + 1 &&
         path.every((segment, index) => item.path[index] === segment) && item.path.at(-1) === String(scriptIndex));
      return <li key={key} className="[overflow-wrap:anywhere]">{typeof id === "string"
         ? <span title={id}>{metadata?.entityName ?? names.get(id.toLowerCase()) ?? id}</span>
         : <FrozenValue value={id} humanizeLabels />}</li>;
   })}</ul>;
}

function isScheduleInteger(value: unknown, max = Infinity): value is number {
   return typeof value === "number" && Number.isInteger(value) && value >= 0 && value <= max;
}

function ScheduleRow({schedule, index, field, presentation, names}: {
   schedule: Record<string, unknown>;
   index: number;
   field: PresentedField;
   presentation: ToolDetailsProps["presentation"];
   names: ReadonlyMap<string, string>;
}) {
   const {t} = useTranslation("ask");
   const {dayOffset, hour, minute, sequenceScripts, ...extras} = schedule;
   const delay = schedule.delayInMinutes;
   const delayed = typeof delay === "number" && Number.isFinite(delay) && delay >= 0;
   if (delayed) {
      // These fields are inactive for delay-based steps, but remain inspectable.
      Object.assign(extras, {dayOffset, hour, minute});
      delete extras.delayInMinutes;
   }
   const validTime = isScheduleInteger(hour, 23) && isScheduleInteger(minute, 59);
   const dayLabel = index === 0 ? "toolApproval.sequence.startAfterDays" : "toolApproval.sequence.thenAfterDays";
   const path = [...field.path, String(index), "sequenceScripts"];
   return <li className="px-3 py-3">
      <div className="flex items-baseline gap-3">
         <span aria-hidden="true" className="w-4 shrink-0 text-xs tabular-nums text-400">{index + 1}</span>
         <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 text-sm font-medium text-800">
               {delayed ? <span>{t(delay === 0 ? "toolApproval.sequence.immediate" : "toolApproval.sequence.afterMinutes", {count: delay})}</span> : <>
                  <span>{isScheduleInteger(dayOffset) ? t(dayLabel, {count: dayOffset}) : <FrozenValue value={{dayOffset}} humanizeLabels />}</span>
                  <span className="tabular-nums">{validTime ? `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}` : <FrozenValue value={{hour, minute}} humanizeLabels />}</span>
               </>}
            </div>
            <div className="mt-1 text-sm leading-relaxed text-600">
               <ScheduleScripts value={sequenceScripts} path={path} fields={presentation.fields} names={names} />
            </div>
            {Object.keys(extras).length > 0 && <details className="mt-2 text-xs text-500"><summary className="cursor-pointer">{t("toolApproval.createScript.settings")}</summary><FrozenValue value={extras} humanizeLabels /></details>}
         </div>
      </div>
   </li>;
}

function ScheduleReview({field, presentation}: {field: PresentedField; presentation: ToolDetailsProps["presentation"]}) {
   const {t} = useTranslation("ask");
   const schedules = field.value as Record<string, unknown>[];
   const names = usePhoneScriptNames(schedules.some(schedule => Array.isArray(schedule.sequenceScripts) && schedule.sequenceScripts.some(id => typeof id === "string")));
   return <section aria-label={t("toolApproval.sequence.schedules")}>
      <h4 className="mb-3 text-xs font-medium text-500">{t("toolApproval.sequence.schedules")} <span className="ml-1 tabular-nums">({schedules.length})</span></h4>
      <ol className="divide-y divide-neutral rounded-lg border border-neutral">
         {occurrenceKeys(schedules, valueKey).map(({item: schedule, key}, index) =>
            <ScheduleRow key={key} schedule={schedule} index={index} field={field} presentation={presentation} names={names} />)}
      </ol>
   </section>;
}

/** Keep the request intact while presenting each scheduled outreach as a readable row. */
export default function SequenceToolDetails({presentation, parameters}: ToolDetailsProps) {
   const bodyPath = isPresentationRecord(parameters.body) ? ["body"] : [];
   const property = (field: PresentedField) => field.path.length === bodyPath.length + 1 ? field.path.at(-1) : undefined;
   const fields = presentedFields(parameters, presentation.fields).map(field => property(field) === "name" ? {...field, emphasis: "primary" as const} : field);
   const schedules = fields.find(field => property(field) === "schedules" && Array.isArray(field.value) && field.value.length > 0 && field.value.every(isPresentationRecord));
   // Nested metadata supplies labels for a schedule; its values are already in that row.
   const visibleFields = schedules ? fields.filter(field => field === schedules || !schedules.path.every((segment, index) => field.path[index] === segment)) : fields;
   const order = ["name", "description", "timezone", "schedules"];
   visibleFields.sort((a, b) => {
      const rank = (field: PresentedField) => {const index = order.indexOf(property(field) ?? ""); return index < 0 ? order.length : index;};
      return rank(a) - rank(b);
   });
   return <div className="px-5 pb-5 pt-4"><ReviewFields fields={visibleFields} renderField={field => field === schedules
      ? <ScheduleReview field={field} presentation={presentation} /> : undefined} /></div>;
}
